import React from 'react';
import * as Styled from './MStarGroup.styles';
import type { MStarGroupProps } from './MStarGroup.types';
import { MStarGroupSize } from './MStarGroup.types';
import { AIcon, AIconSize } from '../../atoms/AIcon';

const COMPONENT_NAME = 'm-web-ui-kit-MStarGroup';
const TEST_ID = 'web-ui-kit-MStarGroup';

const STAR_FILL_ICON = 'icon-star-fill';
const STAR_HALF_ICON = 'icon-star-half-fill';
const STAR_EMPTY_ICON = 'icon-star';

const STAR_GROUP_ICON_SIZE: Record<MStarGroupSize, AIconSize> = {
  [MStarGroupSize.S]: AIconSize.XS,
  [MStarGroupSize.M]: AIconSize.S,
  [MStarGroupSize.L]: AIconSize.M,
};

const getStarCounts = (rating: number) => {
  const cappedRating = Math.min(Math.max(0, rating), 5);
  const fullStars = Math.floor(cappedRating);
  const fraction = cappedRating - fullStars;
  const hasHalfStar = fraction >= 0.25 && fraction < 0.75;
  const displayFullStars = fraction >= 0.75 ? fullStars + 1 : fullStars;
  const emptyStars = 5 - displayFullStars - (hasHalfStar ? 1 : 0);

  return { displayFullStars, hasHalfStar, emptyStars };
};

export const getAIconSizeForStarGroup = (size: MStarGroupSize): AIconSize =>
  STAR_GROUP_ICON_SIZE[size];

export const MStarGroup: React.FC<MStarGroupProps> = ({
  rating = 0,
  size = MStarGroupSize.M,
  className,
  ...rest
}) => {
  const totalStars = 5;
  const iconSize = STAR_GROUP_ICON_SIZE[size];
  const { displayFullStars, hasHalfStar, emptyStars } = getStarCounts(rating);

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={className}
      {...rest}
    >
      {Array.from({ length: displayFullStars }, (_, index) => (
        <Styled.StarIcon key={`filled-${index}`} aria-hidden="true">
          <AIcon icon={STAR_FILL_ICON} size={iconSize} />
        </Styled.StarIcon>
      ))}
      {hasHalfStar && (
        <Styled.StarIcon aria-hidden="true">
          <AIcon icon={STAR_HALF_ICON} size={iconSize} />
        </Styled.StarIcon>
      )}
      {Array.from({ length: emptyStars }, (_, index) => (
        <Styled.StarIcon key={`empty-${index}`} $empty aria-hidden="true">
          <AIcon icon={STAR_EMPTY_ICON} size={iconSize} />
        </Styled.StarIcon>
      ))}
      <Styled.ScreenReaderText>
        {rating} out of {totalStars} stars
      </Styled.ScreenReaderText>
    </Styled.Wrapper>
  );
};
